import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'
import { MessageCircle } from 'lucide-react'
import { useAuth } from '@/contexts/Auth0Context'
import { useFeedSocket } from '@/contexts/FeedSocketContext'
import { feedService } from '@/services/feedService'
import { useToast } from '@/contexts/ToastContext'
import { FeedSocketEvent, ReactionType } from '@/types/feed'
import type {
  IFeedReactionChangedPayload,
  IPost
} from '@/types/feed'
import ReactionBar from './ReactionBar'
import CommentThread from './CommentThread'
import PostActionsMenu from './PostActionsMenu'

interface PostCardProps {
  post: IPost
  /** When true, renders the comment thread inline. Otherwise shows a link. */
  expandComments?: boolean
  onDeleted?: (postId: string) => void
}

const PostCard: React.FC<PostCardProps> = ({ post, expandComments = false, onDeleted }) => {
  const { user } = useAuth()
  const { on } = useFeedSocket()
  const { showToast } = useToast()

  const [liveCounts, setLiveCounts] = useState<Record<ReactionType, number> | undefined>(undefined)
  const [showComments, setShowComments] = useState(expandComments)
  const [commentCount, setCommentCount] = useState(post.commentCount)

  useEffect(() => {
    const off = on<IFeedReactionChangedPayload>(FeedSocketEvent.REACTION_CHANGED, (p) => {
      if (p.postId !== post.id) return
      setLiveCounts(p.reactionCounts)
    })
    return off
  }, [post.id, on])

  const canDelete = user?.id === post.authorId || user?.role === 'admin'

  const handleDelete = async (): Promise<void> => {
    try {
      await feedService.deletePost(post.id)
      onDeleted?.(post.id)
      showToast({ type: 'success', message: 'Post deleted' })
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to delete'
      showToast({ type: 'error', message })
    }
  }

  return (
    <article className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <header className="mb-2 flex items-start justify-between">
        <div className="flex items-center gap-2">
          <Link to={`/users/${post.authorId}`}>
            {post.author.profilePicture ? (
              <img
                src={post.author.profilePicture}
                alt=""
                className="h-9 w-9 rounded-full object-cover"
              />
            ) : (
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-100 text-sm font-medium text-primary-700">
                {post.author.firstName.charAt(0)}
                {post.author.lastName.charAt(0)}
              </div>
            )}
          </Link>
          <div>
            <p className="text-sm font-medium text-gray-900">
              <Link to={`/users/${post.authorId}`} className="hover:underline">
                {post.author.firstName} {post.author.lastName}
              </Link>
            </p>
            <p className="text-xs capitalize text-gray-500">
              {post.author.role} · {formatDistanceToNow(new Date(post.createdAt), { addSuffix: true })}
            </p>
          </div>
        </div>
        <PostActionsMenu canDelete={canDelete} onDelete={handleDelete} />
      </header>

      <Link to={`/feed/posts/${post.id}`} className="block">
        <p className="whitespace-pre-wrap text-sm text-gray-800">{post.content}</p>
      </Link>

      <footer className="mt-3 space-y-3">
        <div className="flex items-center justify-between">
          <ReactionBar post={post} liveCounts={liveCounts} />
          <button
            type="button"
            onClick={() => setShowComments((v) => !v)}
            className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
          >
            <MessageCircle className="h-4 w-4" />
            {commentCount}
          </button>
        </div>

        {showComments && (
          <CommentThread postId={post.id} onCountChange={setCommentCount} />
        )}
      </footer>
    </article>
  )
}

export default PostCard
